import React, { Component, memo } from 'react'
import PropTypes from 'prop-types'
import styled, { keyframes } from 'styled-components'
import { LabelLarge as Label } from '../TextPrimitives'
import _ from 'lodash'
import Button from '../Button'

const OuterWrapper = styled.div`
  display: inline-block;
  width: ${props => props.width}px;
  padding: 4rem 4rem 6rem;
  box-sizing: content-box;
`

const InnerWrapper = styled.div`
  position: relative;
  height: 2rem;
`

const Mark = styled(Label)`
  position: absolute;
  top: 0.5rem;
  left: ${({ xPos }) => xPos * 100}%;
  transform: translateX(-50%);
  color: ${({ theme }) => theme.colors.text.inverse};
  opacity: ${({ soft }) => (soft ? 0.5 : 1)};
  user-select: none;

  &::before {
    content: '';
    position: absolute;
    background: ${({ theme }) => theme.colors.greys.light};
    width: 2px;
    height: 0.5rem;
    top: -0.5rem;
    left: calc(50% - 1px);
  }
`

const pulse = keyframes`
  from {
    transform: scale(1);
  }

  to {
    transform: scale(1.6);
  }
`

const Handle = styled.div.attrs(({ pos, dragging, theme }) => ({
  style: {
    left: pos * 100 + '%',
    transition: dragging ? 'none' : 'left 600ms cubic-bezier(0.86, 0, 0.07, 1)',
  },
}))`
  position: absolute;
  transform: translate(-50%, -0.7rem);
  user-select: none;
  touch-action: none;
  cursor: pointer;
`

const Pulse = styled.div`
  position: absolute;
  top: 0;
  opacity: 0.2;
  background: ${({ theme }) => theme.colors.greys.light};
  width: 5rem;
  height: 5rem;
  border-radius: 5rem;
  animation: ${pulse} 2s cubic-bezier(0.445, 0.05, 0.55, 0.95) alternate
    infinite;
`

class Slider extends Component {
  sliderRef = React.createRef()

  static propTypes = {
    min: PropTypes.number,
    max: PropTypes.number,
    width: PropTypes.number,
    stepSize: PropTypes.number,
    highlightStep: PropTypes.number,
    onChange: PropTypes.func,
  }

  static defaultProps = {
    min: 0,
    max: 10,
    width: 200,
    stepSize: 1,
    highlightStep: 5,
    onChange: x => x,
  }

  state = {
    pos: 0,
    dragging: false,
  }

  static getDerivedStateFromProps({ current, pos, max, min }, state) {
    if (current && current !== state.current) {
      return {
        current,
        pos: _.clamp((current - min) / (max - min), 0, 1),
      }
    }
    return null
  }

  handleDown = e => {
    this.setState({ dragging: true }, () => {
      // TODO: (3) Use pointer API
      document.addEventListener('mousemove', this.handleMove)
      document.addEventListener('mouseup', this.handleUp)
      document.addEventListener('touchmove', this.handleMove)
      document.addEventListener('touchend', this.handleUp)
    })
  }

  handleUp = e => {
    this.setState({ dragging: false }, () => {
      document.removeEventListener('mousemove', this.handleMove)
      document.removeEventListener('mouseup', this.handleUp)
      document.removeEventListener('touchmove', this.handleMove)
      document.removeEventListener('touchend', this.handleUp)
    })
  }

  handleMove = e => {
    this.value =
      ((e.clientX || e.touches[0].clientX) -
        this.sliderRef.current.offsetLeft) /
      this.props.width
  }

  set value(v) {
    if (_.isNumber(v)) {
      const oldValue = this.value
      this.setState({ pos: _.clamp(v, 0, 1) }, () => {
        if (oldValue !== this.value) this.props.onChange(this.value)
      })
    }
  }

  get value() {
    const { pos } = this.state
    const { min } = this.props
    return Math.round(pos * this.diff + min)
  }

  get diff() {
    const { min, max } = this.props
    return max - min
  }

  render() {
    const { dragging, pos } = this.state
    const { min, max, width, stepSize, highlightStep } = this.props
    const diff = this.diff

    const marks = Array.from(
      { length: Math.floor(diff) - 2 },
      (_, i) => min + i + 1
    ).filter(v => v % stepSize === 0)

    const markNodes = [min, ...marks, max].map(v => (
      <Mark
        key={'mark' + v}
        xPos={(v - min) / diff}
        soft={v % highlightStep !== 0 && v !== min && v !== max}
      >
        {v}
      </Mark>
    ))

    return (
      <OuterWrapper width={width}>
        <InnerWrapper ref={this.sliderRef}>
          {markNodes}
          <Handle
            onMouseDown={this.handleDown}
            onTouchStart={this.handleDown}
            onContextMenu={e => {
              e.preventDefault()
            }}
            dragging={dragging}
            pos={pos}
          >
            <Pulse />
            <Button type="round" text={this.value} raised />
          </Handle>
        </InnerWrapper>
      </OuterWrapper>
    )
  }
}

export default memo(Slider)
